import {
  APPOINTMENT_DRAFT_MAX_AMOUNT_MINOR,
  type AppointmentBookingDraftV1,
} from "@/app/booking/core/appointmentBookingDraft";
import {
  appointmentProfessionalSelectionEvidenceMatchesDraft,
  validateAppointmentProfessionalSelectionEvidence,
  type AppointmentProfessionalPerTaskChoiceValue,
  type AppointmentProfessionalSelectionEvidenceV1,
  type AppointmentProfessionalSelectionQuoteV1,
} from "@/app/booking/scheduling/appointmentProfessionalSelection";
import {
  buildAppointmentSchedulePlan,
  type AppointmentSchedulePlan,
} from "@/app/booking/scheduling/appointmentSchedulePlan";
import type { AppointmentStaffChoiceEvidenceV2 } from "@/app/booking/scheduling/appointmentStaffChoiceEvidence";
import {
  appointmentTimeProfessionalMutationEvidenceStructurallyMatchesDraft,
  validateAppointmentTimeProfessionalMutationEvidence,
  type AppointmentTimeProfessionalChoice,
  type AppointmentTimeProfessionalMutationEvidenceV1,
} from "@/app/booking/scheduling/appointmentTimeProfessionalMutation";

export const APPOINTMENT_WAITLIST_PRE_SLOT_QUOTE_SCHEMA_VERSION = 1 as const;

type QuoteSourceChoice =
  | { kind: "any-professional" }
  | { kind: "specific-professional"; userLocationId: string };

export type AppointmentWaitlistPreSlotQuoteSourceV1 =
  | {
      kind: "time-professional-mutation";
      providerId: string;
      evidenceVersion: string;
      taskChoices: readonly { taskId: string; choice: QuoteSourceChoice }[];
    }
  | {
      kind: "professional-selection";
      providerId: string;
      evidenceVersion: string;
      selectedStrategy:
        | { kind: "any-professional" }
        | { kind: "common-professional"; userLocationId: string }
        | {
            kind: "per-task";
            taskChoices: readonly {
              taskId: string;
              choice: QuoteSourceChoice;
            }[];
          };
    }
  | { kind: "step-one" };

export interface AppointmentWaitlistPreSlotLineQuoteV1 {
  participantId: string;
  lineId: string;
  quote: AppointmentProfessionalSelectionQuoteV1;
}

/**
 * Complete pre-slot price authority frozen at Time -> Waitlist. It is never a
 * fallback chain at read time: the highest mutable staff authority present at
 * creation must publish a complete quote or Waitlist fails closed.
 */
export interface AppointmentWaitlistPreSlotQuoteSnapshotV1 {
  schemaVersion: typeof APPOINTMENT_WAITLIST_PRE_SLOT_QUOTE_SCHEMA_VERSION;
  kind: "appointment-waitlist-pre-slot-quote";
  appointmentDraftId: string;
  planVersion: 1;
  planFingerprint: string;
  source: AppointmentWaitlistPreSlotQuoteSourceV1;
  lineQuotes: readonly AppointmentWaitlistPreSlotLineQuoteV1[];
  aggregate: {
    totalAmountMinor: number;
    currency: string;
    priceType: "fixed" | "from" | "free";
    compareAtTotalAmountMinor: number | null;
  };
}

function same(left: unknown, right: unknown) {
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch {
    return false;
  }
}

function hash(value: string) {
  let result = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    result = Math.imul(result ^ value.charCodeAt(index), 16_777_619);
  }
  return (result >>> 0).toString(16).padStart(8, "0");
}

function cloneChoice(
  choice: AppointmentTimeProfessionalChoice | AppointmentProfessionalPerTaskChoiceValue,
): QuoteSourceChoice {
  return choice.kind === "any-professional"
    ? { kind: "any-professional" }
    : {
        kind: "specific-professional",
        userLocationId: choice.userLocationId,
      };
}

function cloneQuote(
  quote: AppointmentProfessionalSelectionQuoteV1,
): AppointmentProfessionalSelectionQuoteV1 {
  return {
    amountMinor: quote.amountMinor,
    currency: quote.currency,
    priceType: quote.priceType,
    compareAtAmountMinor: quote.compareAtAmountMinor,
    savings: quote.savings ? { ...quote.savings } : null,
  };
}

function deriveAggregate(
  lineQuotes: readonly AppointmentWaitlistPreSlotLineQuoteV1[],
) {
  const currency = lineQuotes[0]?.quote.currency ?? null;
  if (
    !currency ||
    lineQuotes.some(({ quote }) => quote.currency !== currency)
  ) {
    return null;
  }
  const totalAmountMinor = lineQuotes.reduce(
    (total, { quote }) => total + quote.amountMinor,
    0,
  );
  const compareAtTotalAmountMinor = lineQuotes.some(
    ({ quote }) => quote.compareAtAmountMinor !== null,
  )
    ? lineQuotes.reduce(
        (total, { quote }) =>
          total + (quote.compareAtAmountMinor ?? quote.amountMinor),
        0,
      )
    : null;
  if (
    !Number.isSafeInteger(totalAmountMinor) ||
    totalAmountMinor > APPOINTMENT_DRAFT_MAX_AMOUNT_MINOR ||
    (compareAtTotalAmountMinor !== null &&
      (!Number.isSafeInteger(compareAtTotalAmountMinor) ||
        compareAtTotalAmountMinor > APPOINTMENT_DRAFT_MAX_AMOUNT_MINOR))
  ) {
    return null;
  }
  return {
    totalAmountMinor,
    currency,
    priceType: lineQuotes.some(({ quote }) => quote.priceType === "from")
      ? ("from" as const)
      : lineQuotes.every(({ quote }) => quote.priceType === "free")
        ? ("free" as const)
        : ("fixed" as const),
    compareAtTotalAmountMinor,
  };
}

function aggregateTaskQuotes(
  quotes: readonly AppointmentProfessionalSelectionQuoteV1[],
) {
  const currency = quotes[0]?.currency ?? null;
  if (!currency || quotes.some((quote) => quote.currency !== currency)) {
    return null;
  }
  if (quotes.length === 1) return cloneQuote(quotes[0]!);
  const amountMinor = quotes.reduce((total, quote) => total + quote.amountMinor, 0);
  const compareAtAmountMinor = quotes.some(
    (quote) => quote.compareAtAmountMinor !== null,
  )
    ? quotes.reduce(
        (total, quote) =>
          total + (quote.compareAtAmountMinor ?? quote.amountMinor),
        0,
      )
    : null;
  if (
    !Number.isSafeInteger(amountMinor) ||
    amountMinor > APPOINTMENT_DRAFT_MAX_AMOUNT_MINOR ||
    (compareAtAmountMinor !== null &&
      (!Number.isSafeInteger(compareAtAmountMinor) ||
        compareAtAmountMinor > APPOINTMENT_DRAFT_MAX_AMOUNT_MINOR ||
        compareAtAmountMinor <= amountMinor))
  ) {
    return null;
  }
  const percentageBasisPoints = quotes.map(
    (quote) => quote.savings?.percentageBasisPoints ?? null,
  );
  const authoredPercentages = percentageBasisPoints.filter(
    (value): value is number => value !== null,
  );
  let aggregatePercentageBasisPoints: number | null = null;
  if (authoredPercentages.length > 0) {
    const authored = authoredPercentages[0]!;
    const savingsAmountMinor =
      compareAtAmountMinor === null
        ? null
        : compareAtAmountMinor - amountMinor;
    const derived =
      savingsAmountMinor === null || compareAtAmountMinor === null
        ? null
        : Number(
            (BigInt(savingsAmountMinor) * BigInt(10_000) +
              BigInt(compareAtAmountMinor) / BigInt(2)) /
              BigInt(compareAtAmountMinor),
          );
    if (
      authoredPercentages.length !== quotes.length ||
      authoredPercentages.some((value) => value !== authored) ||
      derived !== authored
    ) {
      return null;
    }
    aggregatePercentageBasisPoints = authored;
  }
  return {
    amountMinor,
    currency,
    priceType: quotes.some((quote) => quote.priceType === "from")
      ? ("from" as const)
      : quotes.every((quote) => quote.priceType === "free")
        ? ("free" as const)
        : ("fixed" as const),
    compareAtAmountMinor,
    savings:
      compareAtAmountMinor === null
        ? null
        : quotes.every((quote) => quote.savings === null)
          ? null
          : {
              amountMinor: compareAtAmountMinor - amountMinor,
              percentageBasisPoints: aggregatePercentageBasisPoints,
            },
  } satisfies AppointmentProfessionalSelectionQuoteV1;
}

function createTaskLineQuotes(input: {
  plan: AppointmentSchedulePlan;
  quoteByTaskId: ReadonlyMap<string, AppointmentProfessionalSelectionQuoteV1>;
}) {
  if (
    input.quoteByTaskId.size !== input.plan.tasks.length ||
    input.plan.tasks.some((task) => !input.quoteByTaskId.has(task.taskId))
  ) {
    return null;
  }
  const lineQuotes = input.plan.lineQuotes.map((line) => {
    const tasks = input.plan.tasks.filter(
      (task) =>
        task.participantId === line.participantId && task.lineId === line.lineId,
    );
    const quotes = tasks.flatMap((task) => {
      const quote = input.quoteByTaskId.get(task.taskId);
      return quote ? [quote] : [];
    });
    const quote =
      quotes.length === tasks.length ? aggregateTaskQuotes(quotes) : null;
    return quote
      ? { participantId: line.participantId, lineId: line.lineId, quote }
      : null;
  });
  return lineQuotes.some((line) => line === null)
    ? null
    : (lineQuotes as AppointmentWaitlistPreSlotLineQuoteV1[]);
}

function createStepOneLineQuotes(
  plan: AppointmentSchedulePlan,
) {
  return plan.lineQuotes.map((line) => {
    const savingsAmount =
      line.compareAtAmountMinor === null
        ? null
        : line.compareAtAmountMinor - line.amountMinor;
    return {
      participantId: line.participantId,
      lineId: line.lineId,
      quote: {
        amountMinor: line.amountMinor,
        currency: line.currency,
        priceType: line.priceType,
        compareAtAmountMinor: line.compareAtAmountMinor,
        savings:
          savingsAmount === null
            ? null
            : { amountMinor: savingsAmount, percentageBasisPoints: null },
      },
    } satisfies AppointmentWaitlistPreSlotLineQuoteV1;
  });
}

function projectAggregateQuoteOntoStepOneLines(input: {
  plan: AppointmentSchedulePlan;
  quote: AppointmentProfessionalSelectionQuoteV1;
}) {
  const lineQuotes = createStepOneLineQuotes(input.plan);
  const aggregate = deriveAggregate(lineQuotes);
  const expectedSavings =
    aggregate?.compareAtTotalAmountMinor === null || !aggregate
      ? null
      : {
          amountMinor:
            aggregate.compareAtTotalAmountMinor - aggregate.totalAmountMinor,
          percentageBasisPoints: null,
        };
  return aggregate &&
    aggregate.totalAmountMinor === input.quote.amountMinor &&
    aggregate.currency === input.quote.currency &&
    aggregate.priceType === input.quote.priceType &&
    aggregate.compareAtTotalAmountMinor === input.quote.compareAtAmountMinor &&
    same(expectedSavings, input.quote.savings)
    ? lineQuotes
    : null;
}

export function resolveAppointmentWaitlistPreSlotQuoteSnapshot(input: {
  appointmentDraft: AppointmentBookingDraftV1;
  staffChoiceEvidence?: AppointmentStaffChoiceEvidenceV2 | null;
  timeProfessionalMutationEvidence?: AppointmentTimeProfessionalMutationEvidenceV1 | null;
  professionalSelectionEvidence?: AppointmentProfessionalSelectionEvidenceV1 | null;
}): AppointmentWaitlistPreSlotQuoteSnapshotV1 | null {
  const planResult = buildAppointmentSchedulePlan(input.appointmentDraft);
  if (!planResult.ok) return null;
  const plan = planResult.plan;
  const timeValidation = input.timeProfessionalMutationEvidence
    ? validateAppointmentTimeProfessionalMutationEvidence(
        input.timeProfessionalMutationEvidence,
      )
    : null;
  let source: AppointmentWaitlistPreSlotQuoteSourceV1;
  let lineQuotes: AppointmentWaitlistPreSlotLineQuoteV1[] | null;

  if (input.timeProfessionalMutationEvidence) {
    if (
      !timeValidation?.valid ||
      input.staffChoiceEvidence != null ||
      input.professionalSelectionEvidence != null ||
      !appointmentTimeProfessionalMutationEvidenceStructurallyMatchesDraft({
        draft: input.appointmentDraft,
        evidence: timeValidation.evidence,
        locationEligibilityPublication:
          timeValidation.evidence.locationEligibilityPublication,
      })
    ) {
      return null;
    }
    const quoteByTaskId = new Map<string, AppointmentProfessionalSelectionQuoteV1>();
    for (const task of timeValidation.evidence.tasks) {
      const candidates = timeValidation.evidence.quotePublications.filter(
        (publication) =>
          publication.taskId === task.taskId &&
          same(publication.choice, task.initialChoice),
      );
      if (candidates.length !== 1 || quoteByTaskId.has(task.taskId)) return null;
      quoteByTaskId.set(task.taskId, candidates[0]!.quote);
    }
    lineQuotes = createTaskLineQuotes({ plan, quoteByTaskId });
    source = {
      kind: "time-professional-mutation",
      providerId: timeValidation.evidence.providerId,
      evidenceVersion: timeValidation.evidence.evidenceVersion,
      taskChoices: timeValidation.evidence.tasks.map((task) => ({
        taskId: task.taskId,
        choice: cloneChoice(task.initialChoice),
      })),
    };
  } else if (input.professionalSelectionEvidence) {
    const evidenceValidation = validateAppointmentProfessionalSelectionEvidence(
      input.professionalSelectionEvidence,
    );
    const receipt = input.staffChoiceEvidence?.professionalSelection;
    if (
      !evidenceValidation.valid ||
      !receipt ||
      !appointmentProfessionalSelectionEvidenceMatchesDraft({
        draft: input.appointmentDraft,
        evidence: evidenceValidation.evidence,
        locationEligibilityPublication:
          evidenceValidation.evidence.locationEligibilityPublication,
      }) ||
      receipt.providerId !== evidenceValidation.evidence.providerId ||
      receipt.evidenceVersion !== evidenceValidation.evidence.evidenceVersion ||
      receipt.staffEligibilityProviderId !==
        evidenceValidation.evidence.staffEligibilityProviderId ||
      receipt.staffEligibilityEvidenceVersion !==
        evidenceValidation.evidence.staffEligibilityEvidenceVersion ||
      !same(
        receipt.locationEligibilityPublication,
        evidenceValidation.evidence.locationEligibilityPublication,
      )
    ) {
      return null;
    }
    const selectedStrategy = receipt.selectedStrategy;
    const tasks = plan.tasks;
    if (selectedStrategy === "any-professional") {
      const publication = evidenceValidation.evidence.quotePublications.filter(
        ({ selection }) => selection.kind === "any-professional",
      );
      if (
        publication.length !== 1 ||
        tasks.some(({ staffPreference }) => staffPreference.kind !== "any")
      )
        return null;
      lineQuotes =
        plan.lineQuotes.length === 1
          ? [
              {
                participantId: plan.lineQuotes[0]!.participantId,
                lineId: plan.lineQuotes[0]!.lineId,
                quote: cloneQuote(publication[0]!.quote),
              },
            ]
          : projectAggregateQuoteOntoStepOneLines({
              plan,
              quote: publication[0]!.quote,
            });
      source = {
        kind: "professional-selection",
        providerId: evidenceValidation.evidence.providerId,
        evidenceVersion: evidenceValidation.evidence.evidenceVersion,
        selectedStrategy: { kind: "any-professional" },
      };
    } else if (selectedStrategy === "common-professional") {
      const ids = new Set(
        tasks.flatMap(({ staffPreference }) =>
          staffPreference.kind === "specific" &&
          staffPreference.source === "customer-selection"
            ? [staffPreference.userLocationId]
            : [],
        ),
      );
      const userLocationId = ids.size === 1 ? [...ids][0] : null;
      const publications = userLocationId
        ? evidenceValidation.evidence.quotePublications.filter(
            ({ selection }) =>
              selection.kind === "common-professional" &&
              selection.userLocationId === userLocationId,
          )
        : [];
      if (
        !userLocationId ||
        publications.length !== 1 ||
        tasks.some(
          ({ staffPreference }) =>
            staffPreference.kind !== "specific" ||
            staffPreference.source !== "customer-selection" ||
            staffPreference.userLocationId !== userLocationId,
        )
      )
        return null;
      lineQuotes =
        plan.lineQuotes.length === 1
          ? [
              {
                participantId: plan.lineQuotes[0]!.participantId,
                lineId: plan.lineQuotes[0]!.lineId,
                quote: cloneQuote(publications[0]!.quote),
              },
            ]
          : projectAggregateQuoteOntoStepOneLines({
              plan,
              quote: publications[0]!.quote,
            });
      source = {
        kind: "professional-selection",
        providerId: evidenceValidation.evidence.providerId,
        evidenceVersion: evidenceValidation.evidence.evidenceVersion,
        selectedStrategy: { kind: "common-professional", userLocationId },
      };
    } else {
      const quoteByTaskId = new Map<string, AppointmentProfessionalSelectionQuoteV1>();
      const taskChoices: { taskId: string; choice: QuoteSourceChoice }[] = [];
      for (const task of tasks) {
        const choice: AppointmentProfessionalPerTaskChoiceValue =
          task.staffPreference.kind === "any"
            ? { kind: "any-professional" }
            : task.staffPreference.kind === "specific" &&
                task.staffPreference.source === "customer-selection"
              ? {
                  kind: "specific-professional",
                  userLocationId: task.staffPreference.userLocationId,
                }
              : null!;
        if (!choice) return null;
        const publications = evidenceValidation.evidence.quotePublications.filter(
          ({ selection }) =>
            selection.kind === "per-task" &&
            selection.taskId === task.taskId &&
            same(selection.choice, choice),
        );
        if (publications.length !== 1) return null;
        quoteByTaskId.set(task.taskId, publications[0]!.quote);
        taskChoices.push({ taskId: task.taskId, choice: cloneChoice(choice) });
      }
      lineQuotes = createTaskLineQuotes({ plan, quoteByTaskId });
      source = {
        kind: "professional-selection",
        providerId: evidenceValidation.evidence.providerId,
        evidenceVersion: evidenceValidation.evidence.evidenceVersion,
        selectedStrategy: { kind: "per-task", taskChoices },
      };
    }
  } else {
    if (input.staffChoiceEvidence != null) return null;
    lineQuotes = createStepOneLineQuotes(plan);
    source = { kind: "step-one" };
  }

  if (!lineQuotes || lineQuotes.length !== plan.lineQuotes.length) return null;
  const aggregate = deriveAggregate(lineQuotes);
  if (!aggregate) return null;
  return {
    schemaVersion: APPOINTMENT_WAITLIST_PRE_SLOT_QUOTE_SCHEMA_VERSION,
    kind: "appointment-waitlist-pre-slot-quote",
    appointmentDraftId: input.appointmentDraft.draftId,
    planVersion: plan.planVersion,
    planFingerprint: `appointment-plan-v1-${hash(JSON.stringify(plan))}`,
    source,
    lineQuotes,
    aggregate,
  };
}

export function appointmentWaitlistPreSlotQuoteSnapshotMatches(input: {
  appointmentDraft: AppointmentBookingDraftV1;
  snapshot: unknown;
  staffChoiceEvidence?: AppointmentStaffChoiceEvidenceV2 | null;
  timeProfessionalMutationEvidence?: AppointmentTimeProfessionalMutationEvidenceV1 | null;
  professionalSelectionEvidence?: AppointmentProfessionalSelectionEvidenceV1 | null;
}) {
  const expected = resolveAppointmentWaitlistPreSlotQuoteSnapshot(input);
  return expected && same(expected, input.snapshot) ? expected : null;
}

export function haveExactAppointmentWaitlistPreSlotQuoteAuthority(
  left: unknown,
  right: unknown,
) {
  return same(left, right);
}
